import CustomError from '../customError';
import { MessageTypes } from '../types/messageTypes';
import MessageHelper from './messageHelper';

// TP-2327: every scanned document in the api answers with these two codes, not just the letterhead.
const HTTP_STATUS_SCAN_PENDING = 423; // Locked: the document is still being scanned
const HTTP_STATUS_SCAN_GONE = 410;    // Gone: the scan rejected or removed the document

// The reason phrases the api sends for a letterhead (TiaApiProviderException.DefaultReasonphrases).
// They are the only thing that tells a refused export apart from any other refused document.
const API_INVOICE_TEMPLATE_SCAN_PENDING = 'Invoice template scan in progress. Try again later.';
const API_INVOICE_TEMPLATE_SCAN_INFECTED = 'Invoice template was identified as malicious and is no longer available.';

class ErrorHelper {
    static getCustomWebsiteErrorFunction() {
        return (error: any) => {
            throw this.GetCustomErrorObject(error);
        };
    }

    static getCustomWebsiteBlobErrorFunction() {
        return async (error: any) => {
            if (error?.response?.body instanceof Blob) {
                try {
                    const text = await error.response.body.text();
                    error.response.body = JSON.parse(text);
                } catch {
                    // body niet parseerbaar (bv. HTML error page) - laat zoals is, fallback path treedt op
                }
            }
            throw this.GetCustomErrorObject(error);
        };
    }

    // Carries the status because the reducers recognise maintenance mode and an expired session by it. A plain
    // object: the store's serify middleware throws on an Error instance, and redux-catch then swallows the action.
    static getFailedActionPayload(error: any) {
        return {
            message: error?.message,
            status: error?.status
        };
    }

    static showMessageNoRethrow(error: any) {
        const customError = this.GetCustomErrorObject(error);
        MessageHelper.showMessage({type: customError.messageType, message: customError.message});
    }

    static showMessageOnNoContent(customError: CustomError) {
        if(customError.messageType === MessageTypes.INFO)
        {
            MessageHelper.showMessage({type: customError.messageType, message: customError.message});
        }
    }

    private static buildScanRetryHint(error: any) : string {
        const retryAfterSeconds = parseInt(error?.response?.headers?.['retry-after'], 10);
        if(Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            const minutes = Math.ceil(retryAfterSeconds / 60);
            return `Please try again in about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
        }
        return 'Please try again shortly.';
    }

    // CheckMaintenanceModeAttribute double-encodes its body, so it can arrive as a json string instead of an
    // object. Anything else that does not parse (an html error page) is not a body we can read.
    private static readBody(body: any) : any {
        if(!body) return null;
        if(typeof body !== 'string') return body;

        try {
            return JSON.parse(body);
        } catch {
            return null;
        }
    }

    // The api serialises in PascalCase (no camelCase resolver in Startup.cs); camelCase is read too for bodies that
    // bypass the exception filter. Error comes from TiaUnauthorizedResult / TiaServiceUnavailableResult (401/403/503).
    private static getBodyReasonPhrase(body: any) : string | null {
        if(!body) return null;

        const messages = this.collectErrorMessages(body.Errors ?? body.errors ?? body.Error ?? body.error);
        if(messages.length > 0) return messages.join('\r\n');

        return this.firstNonEmptyString(body.Title, body.title);
    }

    // TiaApiException.Message is the status name ("Locked", "BadRequest"), never a phrase written for the
    // user, so it only serves as a last resort - and never as a reason phrase, or a scanned document would be
    // reported to the user as "Locked".
    private static getApiErrorMessage(body: any) : string | null {
        const parsed = this.readBody(body);
        if(!parsed) return null;

        return this.getBodyReasonPhrase(parsed) ?? this.firstNonEmptyString(parsed.Message, parsed.message);
    }

    // Errors holds {Message} for a plain api exception, {property: [...messages]} for a validation error, and
    // {identifier: {property: [...]}} when the validation items carry an identifier - each of those possibly
    // wrapped in an array. Walking it keeps every message without hard-coding a shape.
    private static collectErrorMessages(value: any) : string[] {
        if(value === null || value === undefined) return [];
        if(typeof value === 'string') return value.trim() ? [value] : [];
        if(Array.isArray(value)) return value.reduce((all: string[], item) => all.concat(this.collectErrorMessages(item)), []);
        if(typeof value !== 'object') return [];

        const message = this.firstNonEmptyString(value.Message, value.message);
        if(message) return [message];

        return Object.values(value).reduce((all: string[], item) =>
            all.concat(Array.isArray(item) ? this.collectPropertyMessages(item) : this.collectErrorMessages(item)), []);
    }

    // A property holds every message for that field, and the exception filter puts the FluentValidation
    // CustomState next to each of them. Only the strings are kept: no validator sets a CustomState, so it is null
    // in practice. A string there could not be told apart from a second message (WebApiValidationAttribute sends
    // those as a plain string array), and dropping a real message is the worse of the two.
    private static collectPropertyMessages(messages: any[]) : string[] {
        return messages.filter(m => typeof m === 'string' && m.trim().length > 0);
    }

    private static firstNonEmptyString(...candidates: any[]) : string | null {
        return candidates.find(c => typeof c === 'string' && c.trim().length > 0) ?? null;
    }

    private static getApiReasonPhrase(error: any) : string | null {
        const response = error?.response;
        return this.getBodyReasonPhrase(this.readBody(response?.body))
            ?? this.getBodyReasonPhrase(this.readBody(response?.text));
    }

    // The letterhead is the one scanned document the website words itself: its refusal blocks a pdf export,
    // not a download. Anything else keeps the api's phrase, so an attachment is never reported as a letterhead.
    private static buildScanPendingMessage(error: any) : string {
        const reasonPhrase = this.getApiReasonPhrase(error);
        if(reasonPhrase === API_INVOICE_TEMPLATE_SCAN_PENDING) {
            return `The letterhead is still being scanned. ${this.buildScanRetryHint(error)}`;
        }
        return reasonPhrase ?? `The document is still being scanned. ${this.buildScanRetryHint(error)}`;
    }

    private static buildScanGoneMessage(error: any) : string {
        const reasonPhrase = this.getApiReasonPhrase(error);
        if(reasonPhrase === API_INVOICE_TEMPLATE_SCAN_INFECTED) {
            return 'The letterhead is no longer available, so the export could not be generated.';
        }
        return reasonPhrase ?? 'The document is no longer available.';
    }

    static errorHasStatusCode(error: any, ...statusCodes: number[]) : boolean {
        return statusCodes.map(s => s?.toString()).includes(error?.toString()) ||
               statusCodes.map(s => s?.toString()).includes(error?.status?.toString()) ||
               statusCodes.map(s => s?.toString()).includes(error?.message?.toString());
    }

    private static GetCustomErrorObject(error: any) : CustomError
    {
        // Already mapped: the api layer throws a CustomError and the sagas hand that same object to
        // showMessageNoRethrow. A second pass would only overwrite a correct result, because the status is
        // still 423/410 while the response - and with it the Retry-After header and the body - is gone.
        if(error instanceof CustomError) return error;

        const customError = new CustomError(MessageTypes.ERROR, 'Unknown error occurred.');
        if(error) {
            if(error.status) customError.status = error.status;

            // TP-2327: turn the scan-status codes into a readable message instead of the bare status code.
            // Pending is transient and self-resolving, hence INFO rather than the default error styling.
            if(this.errorHasStatusCode(error, HTTP_STATUS_SCAN_PENDING)) {
                customError.messageType = MessageTypes.INFO;
                customError.message = this.buildScanPendingMessage(error);
                return customError;
            }

            if(this.errorHasStatusCode(error, HTTP_STATUS_SCAN_GONE)) {
                customError.message = this.buildScanGoneMessage(error);
                return customError;
            }

            const response = error.response;
            if(response) {
                // Startup_Auth.RefuseTheToken sets no content-type, so superagent leaves that body in text - every
                // refused or expired token arrives this way.
                const bodyMessage = this.getApiErrorMessage(response.body) ?? this.getApiErrorMessage(response.text);
                if (bodyMessage) {
                    customError.message = bodyMessage;
                    return customError;
                }

                if(response.noContent | response.badRequest | response.notFound) {
                    customError.messageType = MessageTypes.INFO;

                    if(response.noContent) {
                        customError.message = 'No content';
                        return customError;
                    }

                    if(response.badRequest) {
                        customError.message = 'Bad request';
                        return customError;
                    }

                    if(response.notFound) {
                        customError.message ='Not found';
                        return customError;
                    }
                }

                if(error.status) {
                    customError.message = error.status;
                    return customError;
                }
            } else if(error.status) {
                customError.status = error.status;
                if(error.message) customError.message = error.message;
                if(!error.message) customError.message = error.status;
                return customError;
            } else if(error.message) {
                customError.message = error.message;
                return customError;
            }
        }
        return customError;
    }
}

export default ErrorHelper;
